/**
 * Registration page
 */

import { useState, useEffect, FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification } from 'firebase/auth';
// getFirebaseAuth imported dynamically when needed
import { useAuthStore } from '@voxapilot/shared';
import { api } from '../config/firebase';
import { ROUTES } from '../config/constants';
import { cn } from '../utils/cn';
import { getErrorMessage, isSessionApiError } from '../utils/authErrors';
import { Navbar } from '../components/landing/Navbar';

export function RegisterPage() {
  const location = useLocation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  // Check for messages from navigation state
  useEffect(() => {
    const state = location.state as { message?: string; email?: string } | null;
    if (state?.message) {
      setInfoMessage(state.message);
    }
    if (state?.email) {
      setEmail(state.email);
    }
  }, [location]);

  // Redirect if already authenticated (disabled for testing - uncomment when ready)
  // useEffect(() => {
  //   if (!authLoading && isAuthenticated && user && !emailSent) {
  //     // Redirect to app (onboarding guard will handle onboarding flow)
  //     navigate(ROUTES.APP, {replace: true});
  //   }
  // }, [isAuthenticated, user, authLoading, navigate, emailSent]);

  const handleRegister = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!email || !password || !confirmPassword) {
      setError('Please fill in all required fields');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setIsLoading(true);

      // FIXED: Ensure Firebase is initialized before using auth
      const { getFirebaseAuthAsync } = await import('@voxapilot/shared');
      const auth = await getFirebaseAuthAsync();

      // Create user with email/password
      const result = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = result.user;

      // Update profile with name if provided
      if (name.trim()) {
        await updateProfile(firebaseUser, {
          displayName: name.trim(),
        });
      }

      // Send email verification
      try {
        await sendEmailVerification(firebaseUser, {
          url: window.location.origin + ROUTES.SIGN_IN,
          handleCodeInApp: false,
        });
        setEmailSent(true);
      } catch (verificationError) {
        console.warn('Failed to send verification email:', verificationError);
        // Don't block registration if email verification fails
      }

      // Try to create session, but don't fail if it doesn't work
      // Session API is optional - Firebase Auth is sufficient
      // IMPORTANT: Don't delete user account if session API fails!
      let sessionWarning: string | null = null;

      try {
        const token = await firebaseUser.getIdToken();
        const sessionResponse = await api.session.createSession(token);

        if (!sessionResponse.success) {
          // Session API failed, but continue with Firebase Auth
          sessionWarning = 'Session creation failed, but your account was created successfully.';
          console.warn('Session API failed, continuing with Firebase Auth:', sessionResponse.error);
        } else {
          // Session created successfully, try to initialize with session API
          const { initialize } = useAuthStore.getState();
          try {
            await initialize(api.session);
            setIsLoading(false);
            return; // Success with session API
          } catch (initError) {
            console.warn('Session initialization failed, using Firebase Auth directly:', initError);
            sessionWarning = 'Session initialization failed, but your account was created successfully.';
          }
        }
      } catch (sessionError: any) {
        // Session API error - log but don't fail
        if (isSessionApiError(sessionError)) {
          sessionWarning = 'Session creation failed, but your account was created successfully.';
          console.warn('Session API error, continuing with Firebase Auth:', sessionError);
        } else {
          // Non-session error - might be a real problem, but still try to continue
          console.warn('Unexpected error during session creation, continuing with Firebase Auth:', sessionError);
          sessionWarning = 'Session creation had issues, but your account was created successfully.';
        }
      }

      // Continue with Firebase Auth (session API is optional)
      // User account is preserved - don't delete it!
      const { initialize } = useAuthStore.getState();
      try {
        await initialize(); // Use Firebase Auth directly
        setIsLoading(false);

        // Show warning if session API failed, but don't treat it as an error
        if (sessionWarning) {
          console.info(sessionWarning);
          // Optionally show a non-blocking info message to user
          // For now, we'll just log it - user account is created and authenticated
        }
      } catch (initError) {
        // Even Firebase Auth initialization failed - this is a real error
        // But don't delete the user account - it was successfully created
        throw initError;
      }
    } catch (err: any) {
      // Use error message utility for user-friendly messages
      const errorMessage = getErrorMessage(err);
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background dark:bg-[#1E1E1E] flex flex-col">
      {/* Full Navbar */}
      <Navbar />

      {/* Main Content - Centered Card */}
      <div className="flex-1 flex items-center justify-center px-4 pb-12 pt-20">
        <div className="w-full max-w-md">
          {/* Logo - Right above the card */}
          <div className="flex justify-center mb-4">
            <Link to={ROUTES.HOME} className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">V</span>
              </div>
              <span className="font-heading font-bold text-xl text-textPrimary dark:text-[#CCCCCC]">VoxaPilot</span>
            </Link>
          </div>

          {/* Card */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 shadow-sm border border-gray-100 dark:border-gray-700">
            {/* Title */}
            <h1 className="text-2xl font-bold text-textPrimary dark:text-[#CCCCCC] mb-2">
              Create your account
            </h1>

            {/* Description */}
            <p className="text-sm text-textSecondary dark:text-[#858585] mb-6">
              Sign up to get started with VoxaPilot
            </p>

            {/* Info Message */}
            {infoMessage && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-600 dark:text-blue-400">{infoMessage}</p>
              </div>
            )}

            {/* Email Sent Success */}
            {emailSent ? (
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-textPrimary dark:text-[#CCCCCC]">
                  Check your email
                </h2>
                <p className="text-sm text-textSecondary dark:text-[#858585]">
                  We've sent a verification link to <strong className="text-textPrimary dark:text-[#CCCCCC]">{email}</strong>
                </p>
                <p className="text-sm text-textTertiary dark:text-[#6A6A6A]">
                  Please click the link in the email to verify your account.
                </p>
                <div className="pt-4">
                  <Link to={ROUTES.SIGN_IN}>
                    <button
                      className={cn(
                        'w-full py-3 px-4 rounded-lg font-medium',
                        'bg-gray-900 dark:bg-orange-600 text-white',
                        'hover:bg-gray-800 dark:hover:bg-orange-700',
                        'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-orange-500 focus:ring-offset-2',
                        'transition-colors'
                      )}
                    >
                      Go to Sign In
                    </button>
                  </Link>
                </div>
              </div>
            ) : (
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
                    Name (optional)
                  </label>
                  <input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={isLoading}
                    placeholder="Enter your name"
                    className={cn(
                      'w-full px-4 py-3 rounded-lg border',
                      'bg-white dark:bg-[#1E1E1E] text-textPrimary dark:text-[#CCCCCC]',
                      'placeholder:text-gray-400 dark:placeholder:text-[#6A6A6A]',
                      'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-700 focus:border-transparent',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      'transition-colors',
                      error ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'
                    )}
                    autoComplete="name"
                  />
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    placeholder="Enter your email address"
                    className={cn(
                      'w-full px-4 py-3 rounded-lg border',
                      'bg-white dark:bg-[#1E1E1E] text-textPrimary dark:text-[#CCCCCC]',
                      'placeholder:text-gray-400 dark:placeholder:text-[#6A6A6A]',
                      'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-700 focus:border-transparent',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      'transition-colors',
                      error ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'
                    )}
                    autoComplete="email"
                    required
                  />
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    placeholder="Create a password (min. 6 characters)"
                    className={cn(
                      'w-full px-4 py-3 rounded-lg border',
                      'bg-white dark:bg-[#1E1E1E] text-textPrimary dark:text-[#CCCCCC]',
                      'placeholder:text-gray-400 dark:placeholder:text-[#6A6A6A]',
                      'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-700 focus:border-transparent',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      'transition-colors',
                      error ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'
                    )}
                    autoComplete="new-password"
                    required
                  />
                  {password && (
                    <p className="mt-1 text-xs text-textSecondary dark:text-[#858585]">
                      {password.length < 6 ? (
                        <span className="text-orange-600 dark:text-orange-500">Password must be at least 6 characters</span>
                      ) : (
                        <span className="text-green-600 dark:text-green-500">✓ Password meets requirements</span>
                      )}
                    </p>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-textPrimary dark:text-[#CCCCCC] mb-2">
                    Confirm password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                    placeholder="Confirm your password"
                    className={cn(
                      'w-full px-4 py-3 rounded-lg border',
                      'bg-white dark:bg-[#1E1E1E] text-textPrimary dark:text-[#CCCCCC]',
                      'placeholder:text-gray-400 dark:placeholder:text-[#6A6A6A]',
                      'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-700 focus:border-transparent',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      'transition-colors',
                      error || (confirmPassword && password !== confirmPassword) ? 'border-red-300 dark:border-red-700' : 'border-gray-200 dark:border-gray-700'
                    )}
                    autoComplete="new-password"
                    required
                  />
                  {confirmPassword && password && (
                    <p className="mt-1 text-xs">
                      {password !== confirmPassword ? (
                        <span className="text-red-600 dark:text-red-500">Passwords do not match</span>
                      ) : (
                        <span className="text-green-600 dark:text-green-500">✓ Passwords match</span>
                      )}
                    </p>
                  )}
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className={cn(
                    'w-full py-3 px-4 rounded-lg font-medium',
                    'bg-gray-900 dark:bg-orange-600 text-white',
                    'hover:bg-gray-800 dark:hover:bg-orange-700',
                    'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-orange-500 focus:ring-offset-2',
                    'disabled:opacity-50 disabled:cursor-not-allowed',
                    'transition-colors'
                  )}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating account...
                    </span>
                  ) : (
                    'Create account'
                  )}
                </button>
              </form>
            )}

            {/* Footer Links */}
            {!emailSent && (
              <div className="mt-6 text-center space-y-2">
                <p className="text-sm text-textSecondary dark:text-[#858585]">
                  Already have an account?{' '}
                  <Link
                    to={ROUTES.SIGN_IN}
                    className="text-textPrimary dark:text-[#CCCCCC] font-medium underline hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

